import { useEffect, useState, useLayoutEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { lastActive } from "../actions/auth-actions/actions";
import BannerInfo from "../components/BannerInfo/BannerInfo";
import { DiscoverList } from "../components/DiscoverList/DiscoverList";
import LabelList from "../components/Label/LabelList";
import Footer from "../components/Footer/Footer";
import ourCertifications from "../img/certifications.svg";
import jeans from "../img/jeans.webp";
import Header from "../components/Header/Header";
import {
  bannerInfosData,
  bannerInfosDifference,
  bannerInfosCustomization,
} from "../StaticData/BannerInfoData";
import diffImg from "../img/WhatMakesUsDifferent.jpg";
import "../sass/Home.scss";
import { ProductList } from "../components/ProductCard/ProductList";
import { ProductCustomList } from "../components/ProductCard/ProductCustomList";

import { Features } from "../components/Features/Features";
import { DirectOrderCarousel } from "../components/DirectOrderCarousel/DirectOrderCarousel";
import Carousel3D from "../components/Carousel3D/Carousel3D";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { getSelectionProducts } from "../actions/myselection-actions/actions";
import Head from "../components/Head";
import Modal from "antd/lib/modal/Modal";
import { finish, goNextStep } from "../actions/guided-tour-actions/actions";
import { Avatar, Button, List, Skeleton, message } from "antd";
import { NavHashLink } from "react-router-hash-link";
import Popup from "../components/Popup/Popup";
import onboarding_img from "../img/onboarding_img.png";
import customization_tool from "../img/customization_tool.png";
//@ts-ignore
import creative_team from "../video/creative_team.mp4";
import Certification from "../components/Certification/Certification";
import axiosInstance from "../config/axios-instance";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

/* 
  GSAP CDN Links (for reference, currently using npm package):
  <script src="https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/gsap.min.js"></script>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/ScrollTrigger.min.js"></script>
*/

gsap.registerPlugin(ScrollTrigger);

export const Home = () => {
  const mainRef = useRef<HTMLDivElement>(null);
  


  const [width, setWidth] = useState(window.innerWidth);
  const dispatch = useDispatch();
  const { user, isAdmin } = useSelector((state: any) => state.auth);
  const { step } = useSelector((state: any) => state.tour);

  const [homeData, setHomeData] = useState<any>(null);

  
  useLayoutEffect(() => {
    // Hero/Header Animation
    const heroTl = gsap.timeline();
    heroTl.from(".home_header", { opacity: 0, y: -30, duration: 1, ease: "power3.out" })
          .from(".menu__items a", { opacity: 0, y: -20, stagger: 0.1, duration: 0.8, ease: "back.out(1.7)" }, "-=0.6");

    const tweens: any[] = []; // Keep track of tweens for cleanup

    // --- Helper: Split Text Animation ---
    const animateSplitText = (selector: string) => {
      const elements = gsap.utils.toArray(selector);
      elements.forEach((el: any) => {
        if (!el.innerText) return;
        
        // Split text manually into char spans (simple version)
        const text = el.innerText;
        const chars = text.split("").map((char: string) => 
          `<span class="char" style="display:inline-block; opacity:0; transform:translateY(40px)">${char === " " ? "&nbsp;" : char}</span>`
        ).join("");
        
        el.innerHTML = chars;
        
        const t = gsap.to(el.querySelectorAll(".char"), {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: "back.out(1.7)",
          stagger: 0.02,
          scrollTrigger: {
            trigger: el,
            start: "top 85%",
            toggleActions: "play none none reverse"
          }
        });
        tweens.push(t);
      });
    };

    // Apply Split Text to Headings
    // Note: We wait a tick to ensure children (BannerInfo) are rendered if they depend on props, usually useLayoutEffect runs after render.
    // If dynamic data loads later, this might run before text exists. 
    // Since we depend on [homeData], it should work.
    animateSplitText(".banner_container h1, .banner_container h2:not(.h2_subtitle_light_black), .our_label");

    // --- Parallax Effect ---
    // Target images inside features columns
    const parallaxImages = gsap.utils.toArray(".features .col-12 img, .features .col-md-5 img");
    parallaxImages.forEach((img: any) => {
      const t = gsap.fromTo(img, 
        { yPercent: -10 },
        {
            yPercent: 10,
            ease: "none",
            scrollTrigger: {
                trigger: img,
                start: "top bottom",
                end: "bottom top",
                scrub: 1
            }
        }
      );
      tweens.push(t);
    });

    // Content Sections Animation (Revised for compatibility with split text - avoid double animating opacity of container if children animate)
    const sections = ["#for-you", "#direct-order", "#customization", "#creativeteam", ".label_container", ".home_difference"];
    
    sections.forEach((selector) => {
      const elements = gsap.utils.toArray(selector);
      elements.forEach((el: any) => {
        const t = gsap.fromTo(el, 
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power2.out",
            scrollTrigger: {
              trigger: el,
              start: "top 80%",
              toggleActions: "play none none reverse"
            }
          }
        );
        tweens.push(t);
      });
    });

    // --- Carousel 3D Animation ---
    if (document.querySelector(".carousel-3d-container")) {
        const t3d = gsap.fromTo(".carousel-3d-container", 
            { opacity: 0, scale: 0.95 },
            { 
            opacity: 1, 
            scale: 1, 
            duration: 1, 
            scrollTrigger: {
                trigger: ".carousel-3d-container",
                start: "top 75%",
            }
            }
        );
        tweens.push(t3d);
    }

    // --- Background Color Morphing (Proposition 1) ---
    // Define the colors for each section
    const colorStops = [
      { id: ".home_header", color: "#ffffff" },
      { id: "#for-you", color: "#ffffff" },
      { id: "#direct-order", color: "#f4f7f6" }, // Premium Light Gray/Green tint
      { id: "#customization", color: "#ffffff" },
      { id: "#creativeteam", color: "#eff6ff" }, // Very Light Blue tint
      { id: ".label_container", color: "#ffffff" }
    ];

    colorStops.forEach((stop) => {
      const element = document.querySelector(stop.id);
      if(element) {
        ScrollTrigger.create({
          trigger: element,
          start: "top 50%",
          end: "bottom 50%",
          onEnter: () => gsap.to(".home", { backgroundColor: stop.color, duration: 0.8, overwrite: "auto" }),
          onEnterBack: () => gsap.to(".home", { backgroundColor: stop.color, duration: 0.8, overwrite: "auto" }),
        });
      }
    });
    
    // Ensure we reset to white on unmount or scroll back to top if not caught
    tweens.push(gsap.to(".home", { backgroundColor: "#ffffff", paused: true })); // Just to register if needed, but ScrollTrigger mostly handles it.

    // Interactive Elements (Buttons)
    const buttons = gsap.utils.toArray("button, .submit_button, .submit_button_white, .submit_button_blue");
    const cleanupListeners: (() => void)[] = [];

    buttons.forEach((btn: any) => {
      const onEnter = () => gsap.to(btn, { scale: 1.05, duration: 0.3, ease: "elastic.out(1, 0.3)", overwrite: true });
      const onLeave = () => gsap.to(btn, { scale: 1, duration: 0.2, ease: "power2.out", overwrite: true });
      
      btn.addEventListener("mouseenter", onEnter);
      btn.addEventListener("mouseleave", onLeave);
      
      cleanupListeners.push(() => {
        btn.removeEventListener("mouseenter", onEnter);
        btn.removeEventListener("mouseleave", onLeave);
      });
    });
    
    // Cleanup function
    return () => {
        heroTl.kill();
        tweens.forEach(t => {
            if (t.scrollTrigger) t.scrollTrigger.kill();
            t.kill();
        });
        cleanupListeners.forEach(cleanup => cleanup());
    };
  }, [homeData]);


  useEffect(() => {
    const fetchAllData = async () => {
      const token = localStorage.getItem("token") || "";
      const headers = { Authorization: `Bearer ${token}` };

      try {
        const [
          foryouRes,
          directRes,
          customRes,
          creativeRes,
          commitmentRes,
          threeDRes,
          labelsRes,
        ] = await Promise.all([
          axiosInstance.get("/home/foryou", { headers }),
          axiosInstance.get("/home/direct_order", { headers }),
          axiosInstance.get("/home/custom_home", { headers }),
          axiosInstance.get("/home/creative_home", { headers }),
          axiosInstance.get("/home/commitment_home", { headers }),
          axiosInstance.get("/home/three_home", { headers }),
          axiosInstance.get("/home/labels_home", { headers }),
        ]);

        setHomeData({
          foryou: foryouRes.data,
          direct: directRes.data,
          custom: customRes.data,
          creative: creativeRes.data,
          commitment: commitmentRes.data,
          threeD: threeDRes.data,
          labels: labelsRes.data,
        });
      } catch (error) {
        console.error("Error fetching home data:", error);
      }
    };

    fetchAllData();
  }, []);

  useEffect(() => {
    user && !isAdmin && dispatch(getSelectionProducts());
    user && !isAdmin && dispatch(lastActive());
  }, []);

  useEffect(() => {
    const resize: any = window.addEventListener("resize", () => {
      setWidth(window.innerWidth);
    });
    return () => {
      window.removeEventListener("resize", resize);
    };
  }, [width]);

  const videoSource =
    "https://cdn.videvo.net/videvo_files/video/free/2015-07/small_watermarked/A0405_H1505_H2_807_0064_preview.webm";

  // Show AI Preview announcement via Ant Design message (once per user)
/*   useEffect(() => {
    if (localStorage.getItem('ai_preview_alert_read') !== 'true') {
      const key = 'ai-preview-msg';
      message.info({
        key,
        content: (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span>
              <strong>✦ New — AI Preview </strong>
              Try on any product virtually with our new AI‑powered preview.
              Open a product page and click <strong>AI Preview</strong> to get started.
            </span>
            <Button 
              type="primary" 
              size="small" 
              style={{ 
                marginLeft: 16, 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                borderColor: '#fff',
                color: '#fff',
                fontWeight: 'bold',
                borderRadius: '4px'
              }}
              onClick={() => {
                message.destroy(key);
                localStorage.setItem('ai_preview_alert_read', 'true');
              }}
            >
              Close
            </Button>
          </div>
        ),
        duration: 0,
        style: { marginTop: '50px' }
      });
    }
  }, []); */

  return (
    <>
      <div className="home" ref={mainRef}>
        <Head title="DEMCO - COMMITTED TO SUSTAINABILITY" />
        <Header />


        <DiscoverList />
        {/* Products may interest you */}
        <div id="for-you" style={{ position: "relative" }}>
          {!homeData ? (
            <>
              {" "}
              <div className="row  mt-5 mb-5">
                <div className="col-3">
                  {" "}
                  <Skeleton.Avatar active={true} shape={"square"} />
                </div>
                <div className="col-9">
                  {" "}
                  <Skeleton active={true} />
                </div>
              </div>
            </>
          ) : (
            <>
              <Features
                mediaArea={<ProductList />}
                bannerArea={
                  /*
                                                        <BannerInfo bannerInfo={bannerInfosData[0]} type={"for you"}/>
                            */
                  <BannerInfo bannerInfo={homeData.foryou} type={"for you"} />
                }
                mediaSide="left"
                position={"center"}
              />
            </>
          )}
          {user && !isAdmin && !user.last_login && step === 3 && (
            <Popup
              content="Take a deeper look at the products we suggested exclusively for you based on your preferences"
              next_hash="direct-order"
              position="right"
            />
          )}
        </div>

        {/* Direct Order */}
        {!homeData ? (
          <>
            <div className="row  mt-5 mb-5">
              <div className="col-9">
                {" "}
                <Skeleton active={true} />
              </div>
              <div className="col-3">
                {" "}
                <Skeleton.Avatar active={true} shape={"square"} />
              </div>
            </div>
          </>
        ) : (
          <>
            {" "}
            <div id="direct-order" style={{ position: "relative" }}className="mt-5 mb-5">
              <Features
                mediaArea={
                  <DirectOrderCarousel
                    directContent={homeData.direct}
                    type={"direct-order"}
                  />
                }
                bannerArea={
                  <BannerInfo
                    bannerInfo={homeData.direct}
                    type={"direct orders"}
                  />
                }
                mediaSide="right"
                position={"center"}
              />
              {user && !isAdmin && !user.last_login && step === 4 && (
                <Popup
                  content="Create your desired articles and options in collaboration with you team members, save it for later or place your order in one click"
                  next_hash="creative-station"
                  position="left"
                />
              )}
            </div>
          </>
        )}

        <div id="customization" style={{ position: "relative" }}  className="mt-5 mb-5">
          {!homeData ? (
            <>
              <div className="row  mt-5 mb-5">
                <div className="col-3">
                  {" "}
                  <Skeleton.Avatar active={true} shape={"square"} />
                </div>
                <div className="col-9">
                  {" "}
                  <Skeleton active={true} />
                </div>
              </div>
            </>
          ) : (
            <>
              <Features
                mediaArea={<ProductCustomList customContent={homeData.custom} />}
                bannerArea={
                  <BannerInfo
                    bannerInfo={homeData.custom}
                    type={"customization"}
                  />
                }
                mediaSide="left"
                position={"center"}
              />
            </>
          )}
          {user && !isAdmin && !user.last_login && step === 2 && (
            <Popup
              content="Customize your favorite products, play with colors and styles add your own touch and place your order in one click"
              next_hash="for-you"
              position="left"
            />
          )}
        </div>

        <div id="creativeteam" style={{ position: "relative" }}>
          {!homeData ? (
            <>
              <div className="row  mt-5 mb-5">
                <div className="col-9">
                  {" "}
                  <Skeleton active={true} />
                </div>
                <div className="col-3">
                  {" "}
                  <Skeleton.Avatar active={true} shape={"square"} />
                </div>
              </div>
            </>
          ) : (
            <>
              <Features
                mediaArea={
                  <DirectOrderCarousel
                    directContent={homeData.creative}
                    type={"creative"}
                  />
                }
                bannerArea={
                  <BannerInfo
                    bannerInfo={homeData.creative}
                    type={"Creative team"}
                  />
                }
                mediaSide="right"
                position={"center"}
              />
            </>
          )}
          {user && !isAdmin && !user.last_login && step === 4 && (
            <Popup
              content="Create your desired articles and options in collaboration with you team members, save it for later or place your order in one click"
              next_hash="creative-station"
              position="left"
            />
          )}
        </div>

        {/* <div className="row home_difference mt-5">

                    <div className="col-12 col-md-5  home_banner_wrapper_difference ">
                      <BannerInfo bannerInfo={creativeContent}/>
                        <BannerInfo bannerInfo={bannerInfosDifference}/>
                    </div>
                    <div className="col-12 col-md-7  home_difference_img  ">

                        <div
                            className="video_wrapper"
                            dangerouslySetInnerHTML={{
                                __html: `
      <video id="videoBGCreativeStation"  webkit-playsinline="true" autoplay="true"    preload="metadata" muted="true"  loop playsinline="true" id="video-id-auto">
      <source src=${process.env.REACT_APP_STORAGE_URL}${creativeContent?.media} type="video/mp4" ></source>
     </video>
     
    `,
                            }}
                        />
                    </div>

                </div>*/}

        <Carousel3D
          width={width}
          commitmentData={homeData?.commitment}
          threeData={homeData?.threeD}
        />

        <LabelList width={width} labelsData={homeData?.labels} />
        <div className="label_container">
          <div className="label_title">
            {width >= 768 && <div className="blue_line"></div>}
            <h2 className="our_label text-center">Our certifications</h2>
          </div>

          <Certification />
        </div>
        <Footer />
        <Modal
          centered
          className="onboarding-modal"
          visible={user && !isAdmin && !user.last_login && step === 1}
          bodyStyle={{ padding: "0px" }}
          closable={false}
          width={width < 768 ? "350px" : "650px"}
          footer={null}
          // footer={[
          //   <Button
          //     style={{
          //       borderRadius: "5px",
          //     }}
          //     onClick={() => dispatch(finish())}
          //   >
          //     Skip
          //   </Button>,
          //   <NavHashLink
          //     smooth
          //     to="/home#customization"
          //     onClick={() => dispatch(goNextStep())}
          //     className="ml-3"
          //   >
          //     <Button
          //       style={{
          //         background: "#00468c",
          //         borderColor: "#00468c",
          //         borderRadius: "5px",
          //         color: "white",
          //       }}
          //     >
          //       Continue
          //     </Button>
          //   </NavHashLink>,
          // ]}
        >
          <div
            style={{
              height: width < 768 ? "450px" : "280px",
              display: "grid",
              gridTemplateColumns: width < 768 ? "1fr" : "0.45fr 1fr",
              gridAutoRows: width < 768 ? "1fr" : "auto",
              columnGap: "2rem",
              placeItems: "center",
            }}
            className="p-3 p-md-0"
          >
            <div
              style={{ height: "100%", width: "100%", position: "relative" }}
              className={width < 768 ? "img-onboarding" : undefined}
            >
              <img
                src={onboarding_img}
                style={{
                  position: "absolute",
                  bottom: "0px",
                  left: width < 768 ? "50%" : "0px",
                  width: width < 768 ? "57%" : "120%",
                  transform: width < 768 ? "translateX(-50%)" : "none",
                }}
              />
            </div>
            <div
              className="pt-0 pt-md-2 p-md-2"
              style={{ fontFamily: "Poppins" }}
            >
              <h2
                style={{
                  fontSize: width < 768 ? "1.2rem" : "1.5rem",
                  fontWeight: "bold",
                  color: "#00468c",
                }}
              >
                Welcome to the ID Webshop.
              </h2>
              <p
                className="mb-0 mt-3"
                style={{
                  fontWeight: 500,
                  color: "black",
                  fontSize: width < 768 ? "0.8rem" : "0.9rem",
                }}
              >
                We've put together a step-by-step guide
              </p>
              <p
                className="mb-4"
                style={{
                  fontWeight: 500,
                  color: "black",
                  fontSize: width < 768 ? "0.8rem" : "0.9rem",
                }}
              >
                which covers everything you need to get going
              </p>
              <div
                style={{
                  display: "flex",
                  flexDirection: width < 768 ? "column-reverse" : "row",
                }}
              >
                <button
                  style={{
                    borderRadius: "5px",
                    border: "1px solid black",
                    background: "white",
                    color: "black",
                    textTransform: "uppercase",
                    flexGrow: width < 768 ? 1 : 0.33,
                  }}
                  className="py-2 mr-0 mr-md-3 mt-2 mt-md-0"
                  onClick={() => dispatch(finish())}
                >
                  Skip
                </button>
                <NavHashLink
                  smooth
                  to="/home#customization"
                  onClick={() => dispatch(goNextStep())}
                  style={{
                    borderRadius: "5px",
                    border: "1px solid #00468c",
                    background: "#00468c",
                    color: "white",
                    textTransform: "uppercase",
                    fontWeight: "bold",
                    flexGrow: width < 768 ? 1 : 0.66,
                    textAlign: "center",
                    textDecoration: "none",
                  }}
                  className="py-2"
                >
                  Show me
                </NavHashLink>
              </div>
            </div>
          </div>
        </Modal>
      </div>
    </>
  );
};
